// ./src/components/features/Weather/eventWeatherWindow.ts
//
// ROADMAP #182 — WHETHER the event dashboard shows weather, decided ONCE: the forecast request, the top card's chip and
// the Weather card all follow it.
//   • An INDOOR event shows none, unless the host marked it weather-sensitive (owner, 2026-10-20: "for indoor events, we
//     may want to disable by default the weather widget and the forecast in the dashboard's main section").
//   • An event that has ended shows none.
//   • An event further out than the forecast reaches shows NO forecast, only when one will appear. It used to show
//     TODAY's weather in the top card and the next ten days on the card — both read as the event's weather.
//   • Otherwise the forecast covers the event's first day, and it is shown.
// ⓘ Pure (no React, no API client), so `check-dashboard-widgets` runs it.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** How many days the daily forecast reaches, today included (the API's GoogleWeatherForecastGateway: up to 10). */
export const EVENT_FORECAST_DAYS = 10;

export const EVENT_WEATHER = {
  /** Indoor and not weather-sensitive — no weather at all. */
  OFF: 'off',
  /** No start time to look a forecast up for. */
  NONE: 'none',
  /** The event has ended. */
  PAST: 'past',
  /** The event's first day is beyond the forecast — say when it will appear. */
  FAR: 'far',
  /** The forecast reaches the event's first day (or the event is under way). */
  COVERED: 'covered',
} as const;

export type EventWeatherWindow = (typeof EVENT_WEATHER)[keyof typeof EVENT_WEATHER];

export interface EventWeatherInput {
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  /** `event.environment` is indoor (the caller compares it with EVENT_ENVIRONMENT). */
  indoor: boolean;
  weatherSensitive?: boolean | null;
}

export const eventWeatherWindow = (event: EventWeatherInput, now: Date = new Date()): EventWeatherWindow => {
  if (event.indoor && !event.weatherSensitive) {
    return EVENT_WEATHER.OFF;
  }

  if (!event.startsAt) {
    return EVENT_WEATHER.NONE;
  }

  const start = dayjs(event.startsAt);
  const end = event.endsAt ? dayjs(event.endsAt) : start.add(2, 'hour');

  if (!end.isAfter(now)) {
    return EVENT_WEATHER.PAST;
  }

  // Both days on the EVENT's calendar: "today" and the event's first day can be different dates in UTC.
  const tz = event.timezone || dayjs.tz.guess();
  const today = dayjs(now).tz(tz).format('YYYY-MM-DD');
  // ⚠ Stepped on the calendar (a UTC date string), not by 24-hour periods — a day is not 24 hours across a DST change.
  const lastCovered = dayjs.utc(today).add(EVENT_FORECAST_DAYS - 1, 'day').format('YYYY-MM-DD');
  const firstDay = start.tz(tz).format('YYYY-MM-DD');

  return firstDay > lastCovered ? EVENT_WEATHER.FAR : EVENT_WEATHER.COVERED;
};
